import { format } from 'date-fns'
import { toCalendarDate, type MonthBar } from '@/components/calendar/month-grid'
import { STAY_FOCUS_RING } from '@/components/calendar/stay-bars'
import { cn } from '@/lib/utils'

/**
 * A night nobody can book, drawn grey and dashed: not a guest, and not
 * something to act on from here.
 *
 * Shared by the cleaning job calendar (an Uplisting hold) and the property
 * calendar (an owner block as well): the same hold must not look like a stay on
 * one screen and like nothing on the next.
 */
export const BLOCK_BAR_CLASSES = 'border-dashed bg-surface2 border-textFaint text-textMuted'

/**
 * A hold made in the Uplisting app, on the property Calendar tab: diagonal
 * stripes, because it is read-only there and must not look like an owner block
 * that can be removed.
 */
export const HOLD_BAR_CLASSES =
  'border-slate-400/70 text-slate-200 bg-[repeating-linear-gradient(135deg,rgba(148,163,184,0.34)_0_4px,rgba(148,163,184,0.08)_4px_8px)]'

export interface CalendarBlockLike {
  id: string
  /** First blocked day, `YYYY-MM-DD`. */
  start: string
  /** The first day free again, `YYYY-MM-DD`. */
  end_exclusive: string
  kind: 'owner' | 'uplisting'
  note: string | null
  sync_status: string | null
}

const KIND_LABEL: Record<CalendarBlockLike['kind'], string> = {
  owner: 'Owner block',
  uplisting: 'Blocked in Uplisting',
}

/** What a block is called, on its bar and in the agenda. */
export function blockLabel(kind: CalendarBlockLike['kind']): string {
  return KIND_LABEL[kind]
}

export interface BuildBlockBarsOptions {
  /** Stripes for an Uplisting-app hold, and a click on any bar. Left out, every bar is the plain dashed one. */
  onOpen?: (block: CalendarBlockLike) => void
  /** `kind:id` of the block whose panel is open, so its bar is ringed. */
  focusedKey?: string | null
}

export function blockKey(block: Pick<CalendarBlockLike, 'kind' | 'id'>): string {
  return `${block.kind}:${block.id}`
}

export function buildBlockBars(
  blocks: CalendarBlockLike[],
  { onOpen, focusedKey = null }: BuildBlockBarsOptions = {}
): MonthBar[] {
  return blocks.map(block => {
    const start = toCalendarDate(block.start)
    const endExclusive = toCalendarDate(block.end_exclusive)
    const label = KIND_LABEL[block.kind]
    const span = `${format(start, 'd MMM')} to ${format(endExclusive, 'd MMM')}`
    const sync = block.sync_status ? `Uplisting: ${block.sync_status}` : null
    const detail = [block.note, sync].filter(Boolean).join('. ')
    const shown = block.note ? `${label}: ${block.note}` : label
    return {
      key: `block-${block.kind}-${block.id}`,
      start,
      endExclusive,
      label: shown,
      title: `${label}, ${span}${detail ? `. ${detail}` : ''}`,
      testId: 'calendar-block-bar',
      data: { 'data-block-kind': block.kind },
      className: cn(
        onOpen && block.kind === 'uplisting' ? HOLD_BAR_CLASSES : BLOCK_BAR_CLASSES,
        focusedKey === blockKey(block) && STAY_FOCUS_RING
      ),
      // Named like the stay and gap bars, so a week where the label is hidden
      // still says what opens; it starts with the words on the bar.
      ...(onOpen
        ? { onClick: () => onOpen(block), ariaLabel: `${shown}, ${span}${sync ? `. ${sync}` : ''}` }
        : {}),
    }
  })
}
